export { Alert, ErrorAlert };

class Alert {
	/**
	 * @param {Object} parentElement The DOM element to render the alert within.
	 * @param {Object} options An optional object of configuration settings for the alert. See individual object properties for their default options.
	 * @param {String} [options.type] An optional string defining what type of alert to render. One of: 'info', 'success', 'warning', 'danger'. Defaults to 'success'.
	 * @param {String} [options.classList] An optional string of additional classes applied to the alert.
	 * @param {Boolean|Object} [options.scroll] An optional boolean determining whether to scroll the alert into view, or a DOM element to scroll within. Default to true.
	 */
	constructor(parentElement, options = { id: 'Alert', type: 'success', classList: '', scroll: true }) {

		this.id = 'ErrorAlert';
		this.selector = `#${this.id}`;

		this.parent = parentElement;
		this.type = options.type;
		this.classList = options.classList;
		this.scroll = options.scroll;
	}

	/**
	 * Returns an icon classList string based on the alert's type.
	 */
	get icon() {
		switch (this.type) {
			case 'info':
				return 'bi-info-circle';
			case 'success':
				return 'bi-check-circle';
			case 'warning':
				return 'bi-exclamation-circle';
			case 'danger':
				return 'bi-exclamation-circle';
		}
	}

	/**
	 * Renders the supplied content in a DOM alert.
	 * @param {String|Object} options The text or HTML markup rendered to the alert content, or an object of configuration settings for the alert. See individual object properties for their default options.
	 * @param {String} content The text or HTML markup rendered to the alert.
	 * @param {String} [options.title] The bold text rendered before the content of the alert.
	 * @param {String} [options.type] An optional string defining what type of alert to render. One of: 'info', 'success', 'warning', 'danger'. Defaults to 'success'.
	 */
	show(content, options = { title: '' }) {

		//destroy the alert when it already exists
		if (this.parent.querySelector(this.selector)) {
			this.parent.querySelector(this.selector).remove();
		}

		//render alert
		if (!content) {
			console.error('Failed to render Alert: content was not provided.')
			return;
		}

		const alert = document.createElement('div');
		alert.id = this.id;
		alert.classList = `alert alert-${this.type}`;
		if (this.classList) {
			alert.classList += ` ${this.classList}`;
		}
		alert.setAttribute('role', 'alert');
		alert.innerHTML = content;

		const icon = document.createElement('i');
		icon.classList = `bi ${this.icon} me-2`;
		icon.setAttribute('aria-hidden', true);

		const title = document.createElement('strong');
		if (options.title) {
			title.innerHTML = `${options.title}: `;
		}
		title.prepend(icon);
		alert.prepend(title);

		this.parent.insertBefore(alert, this.parent.firstChild);


		if (this.scroll instanceof HTMLElement) {
			//scroll the provided element
			let top = -(this.scroll.getBoundingClientRect().top - alert.getBoundingClientRect().top);
			this.scroll.scrollBy({
				top: top - 40,
				behavior: 'smooth'
			});

		} else if (this.scroll) {
			//scroll to the error alert
			window.scrollBy({
				top: alert.getBoundingClientRect().top - 80,
				behavior: 'auto'
			});
		}
	}
}

class ErrorAlert extends Alert {
	/**
	 * @param {Object} parentElement The DOM element to render the error alert within.
	 * @param {Object} options An optional object of configuration settings for the error alert. See individual object properties for their default options.
	 * @param {String} [options.type] An optional string defining what type of alert to render. One of: 'info', 'success', 'warning', 'danger'. Defaults to 'danger'.
	 * @param {String} [options.source] An optional string defining the expected format of the errors argument, defaults to 'ModelState'.
	 * @param {String} [options.classList] An optional string of additional classes applied to the alert.
	 * @param {Boolean|Object} [options.scroll] An optional boolean determining whether to scroll the error alert into view, or a DOM element to scroll within. Default to true.
	 */
	constructor(parentElement, options = { id: 'ErrorAlert', type: 'danger', source: 'ModelState', classList: '', scroll: true }) {
		super(parentElement, options);
		this.source = options.source;
		this.delimiter = '<br>';
	}

	/**
	 * Renders the supplied errors in a DOM alert.
	 * @param {Object|Array} errors The collection of errors rendered to the alert. Expected format based on the [options.source] argument.
	 */
	show(errors) {

		//prepare errors for rendering based on their format
		let content;
		switch (this.source) {
			case 'ModelState':
				content = this.#fromModelState(errors);
				break;
			case 'Array':
				content = errors.join(this.delimiter);
				break;
		}

		super.show(content);
	}

	/**
	 * Generates a HTML string of errors parsed from the supplied server-side ModelState object.
	 * @param {Object} modelState The .NET ModelState object that contains server-side errors.
	 */
	#fromModelState(modelState) {
		const fields = Object.keys(modelState);

		//get an array of non-empty errors
		const errors = fields.reduce((items, field) => {
			const errors = modelState[field].errors;
			if (errors?.length) {

				if (errors.length == 1) {
					items.push(`<strong>${this.#getFieldLabel(field)}:</strong> ${errors[0].errorMessage}`);

				} else {
					items.push(`<strong>${this.#getFieldLabel(field)}:</strong>`);
					for (const error of errors) {
						items.push(`<span class="ps-4">- ${error.errorMessage}</span>`);
					}
				}
			}
			return items;
		}, []);

		return errors.join(this.delimiter);
	}

	/**
	 * Attempts to get the innerText of a form control's associated label. Defaults to the control's name attribute.
	 * @param {String} name The name attribute of the form control.
	 */
	#getFieldLabel(name) {
		const field = document.querySelector(`[name="${name}"]`);
		if (field) {
			const label = document.querySelector(`label[for="${field.id}"]`);
			if (label) {
				return label.innerText;
			}
		}
		return name;
	}
}
